/**
 * 분리선택형 옵션 핸들러
 * fetchOption으로 옵션을 보여주되 첫번째 옵션부터 순서대로 선택하도록 강요
 */
import { BaseOptionHandler } from './BaseOptionHandler';

export class SeparatedOptionHandler extends BaseOptionHandler {
    /**
     * 옵션 선택 가능 여부 확인
     * 이전 옵션이 모두 선택되어야 다음 옵션 선택 가능
     */
    canSelectOption(optionIndex, productOptions) {
        // 첫 번째 옵션은 항상 선택 가능
        if (optionIndex === 0) {
            return true;
        }

        // 이전 옵션들이 모두 선택되어 있는지 확인
        for (let i = 0; i < optionIndex; i++) {
            const option = productOptions[i];
            if (option.required) {
                if (option.isTextOption) {
                    if (!option.selectedValue || option.selectedValue.trim() === '') {
                        return false;
                    }
                } else {
                    if (option.selectedIndex === null) {
                        return false;
                    }
                }
            }
        }

        return true;
    }

    infoFetchFailedToast() {
        this.setToastMessage('상품 정보를 불러오는데 실패했어요');
        this.setIsToastVisible(true);
    }

    /**
     * 옵션 선택 시: 순서대로 선택하도록 강제
     */
    async onOptionSelect(props) {
        const { 
            optionIndex,
            selectedIdx,
            productOptions,
            renderableOptions,
            textAndFileOptions,
            allVariants,
            optionSelectedProductList,
            setProductOptions,
            setRenderableOptions,
            setOptionSelectedProductList,
            fetchOptionVariant,
            initialProductOptions,
        } = props;
        // 선택 가능 여부 확인
        if (!this.canSelectOption(optionIndex, productOptions)) {
            return;
        }

        const selectedOption = productOptions[optionIndex];
        // A. 이번 선택이 required 인지 확인
            // A-false, 독립형처럼 바로 variant 호출 후 상품 추가
        if (!selectedOption.required) {
            const unrequiredCombination = selectedOption?.optionName + ' - ' + selectedOption.value[selectedIdx]?.optionText;
            if (!unrequiredCombination) {
                // combination 생성 실패
                return;
            }
            // 이미 존재하는 combination이면 그냥 종료
            if (optionSelectedProductList.some(product => product.combination === unrequiredCombination)) {
                this.combinationAlreadyExistsToast();
                return;
            }
            // 최대 품목 개수 체크
            if (optionSelectedProductList.length === this.MAX_QUANTITY) {
                this.maxNumberOfProductsToast();
                return;
            }
            try {
                const variantResponse = await fetchOptionVariant({ 
                    itemId: this.itemId, 
                    options: { [selectedOption.optionName]: { value: selectedOption.value[selectedIdx]?.optionText, required: false } }
                });
                const variantId = variantResponse?.variant_id || '';
                if (variantId.length === 0) {
                    this.infoFetchFailedToast();
                    return;
                }
                // 상품 추가
                setOptionSelectedProductList(prev => [...prev, {
                    productName: selectedOption.value[selectedIdx]?.optionText,
                    combination: unrequiredCombination,
                    noOptionText: true,
                    optionValueList: [selectedOption.value[selectedIdx]?.optionText],
                    textOptions: textAndFileOptions,
                    quantity: 1,
                    setQuantity: (quantity) => this.handleQuantityChange(unrequiredCombination, quantity),
                    price: this.productInfo?.price,
                    additionalPrice: variantResponse?.additional_price || 0,
                    processProductDeleteClicked: () => this.handleProductDeleteClicked(unrequiredCombination),
                    variantId: variantId,
                }]);
            } catch (error) {
                console.error('Failed to fetch variant code:', error);
                this.infoFetchFailedToast();
                return;
            }
            return;     // A-false로 종료
        }

        // A-true: required 이면 
        // B. 이번 선택이 원래 채워져 있던 값을 바꾸는 것인지 확인
            // B-true, 이후 옵션 초기화 (선택 값과 선택지 모두 초기화), 이번 옵션 값 업데이트
        if (selectedOption.selectedIndex !== null) {
            // 이 다음 옵션들 초기화, 이번 옵션 값 업데이트
            // 업데이트된 상태를 사용하기 위해 함수형 업데이트 내에서 모든 로직 처리
            setProductOptions(prev => {
                const updatedOptions = prev.map((option, index) => index > optionIndex ? ({
                    ...option,
                    selectedIndex: null,
                    selectedValue: option.isTextOption ? '' : null,
                    isFocused: false,
                    value: this.initialProductOptions[index].value
                }) : index === optionIndex ? ({
                    ...option,
                    selectedIndex: selectedIdx,
                    selectedValue: option.value[selectedIdx]?.optionText || '---'
                }) : option);
                
                // 현재까지 선택된 옵션들(0부터 optionIndex까지)로 variant 필터링
                const selectedOptionsKeyValueMap = {};
                for (let i = 0; i <= optionIndex; i++) {
                    const option = updatedOptions[i];
                    if (option.selectedIndex !== null) {
                        selectedOptionsKeyValueMap[option.optionName] = option.value[option.selectedIndex]?.optionText || '---';
                    }
                }
                
                const filteredVariants = allVariants.filter(variant => {
                    return Object.entries(selectedOptionsKeyValueMap).every(([key, value]) => variant.options[key] === value);
                });
                
                // 다음 옵션이 있으면 선택지 필터링
                if (optionIndex + 1 < updatedOptions.length) {
                    const nextOption = updatedOptions[optionIndex + 1];
                    const prevNextOptionValue = nextOption.value;
                    const newNextOptionValue = prevNextOptionValue.filter(value => 
                        filteredVariants.some(variant => variant.options[nextOption.optionName] === value.optionText)
                    );
                    const newNextOptionValueWithSoldout = newNextOptionValue.map(value => {
                        const allFilteredVariantsWithValue = filteredVariants.filter(variant => variant.options[nextOption.optionName] === value.optionText);
                        if (allFilteredVariantsWithValue.every(variant => variant.soldout)) {
                            return {
                                ...value,
                                soldout: true
                            };
                        } else {
                            return {
                                ...value,
                                soldout: false
                            };
                        }
                    });
                    updatedOptions[optionIndex + 1] = { 
                        ...nextOption,
                        value: newNextOptionValueWithSoldout
                    };
                }
                
                return updatedOptions;
            });
            return;     // B-true로 종료
        }
        
        // B-false: 다음 값을 선택한 것이면
        // C. 이번 선택으로 모든 required 옵션이 다 채워지는지 확인
            // C-true, 옵션 값 초기화 및 combination 생성
            // 아직 optionSelectedProductList 에 없는 combination 이면 variant 호출 후 상품 추가
        const requiredOptionsFilledWithCurrentSelection = 
            productOptions.every((option, index) => {
                if (option.required) {
                    return (option.selectedIndex !== null) || (index === optionIndex);
                } else {
                    return true;
                }
            });
        if (requiredOptionsFilledWithCurrentSelection) {
            const requiredProductOptionsWithCurrentSelection = productOptions.map((option, index) => index === optionIndex ? ({
                ...option,
                selectedIndex: selectedIdx,
                selectedValue: option.value[selectedIdx]?.optionText || '---'
            }) : option).filter(option => option.required);
            const requiredCombination = requiredProductOptionsWithCurrentSelection.map(option => option.selectedValue).join('-');
            if (!requiredCombination) {
                // combination 생성 실패
                return;
            }
            // 이미 존재하는 combination이면 그냥 종료
            if (optionSelectedProductList.some(product => product.combination === requiredCombination)) {
                this.combinationAlreadyExistsToast();
                return;
            }
            // 최대 품목 개수 체크
            if (optionSelectedProductList.length === this.MAX_QUANTITY) {
                this.maxNumberOfProductsToast();
                return;
            }
            try {
                const variantResponse = await fetchOptionVariant({ 
                    itemId: this.itemId, 
                    options: requiredProductOptionsWithCurrentSelection.reduce((acc, option) => {
                        acc[option.optionName] = { value: option.selectedValue, required: option.required };
                        return acc;
                    }, {})
                });
                const variantId = variantResponse?.variant_id || '';
                if (variantId.length === 0) {
                    this.infoFetchFailedToast();
                    return;
                }
                // 상품 추가
                setOptionSelectedProductList(prev => [...prev, {
                    productName: this.productInfo?.name,
                    combination: requiredCombination,
                    noOptionText: false,
                    optionValueList: requiredProductOptionsWithCurrentSelection.map(option => option.selectedValue),
                    textOptions: textAndFileOptions,
                    quantity: 1,
                    setQuantity: (quantity) => this.handleQuantityChange(requiredCombination, quantity),
                    price: this.productInfo?.price,
                    additionalPrice: variantResponse?.additional_price || 0,
                    processProductDeleteClicked: () => this.handleProductDeleteClicked(requiredCombination),
                    variantId: variantId,
                }]);
                // 전체 옵션 초기화
                setProductOptions(initialProductOptions);
            } catch (error) {
                console.error('Failed to fetch variant code:', error);
                this.infoFetchFailedToast();
                return;
            }
            return;     // C-true로 종료
        }

        // C-false: 아직 required 옵션이 채워지는 중이면
            // productOptions 업데이트
            // allVariants 리스트에서 선택 옵션 키-값 조합이 포함된 variant만 필터링
            // 필터링된 variant 리스트로 다음 옵션의 선택지(productOptions 의 value 리스트) 필터링
        setProductOptions(prev => {
            const updatedOptions = prev.map((option, index) => index === optionIndex ? ({
                ...option,
                selectedIndex: selectedIdx,
                selectedValue: option.value[selectedIdx]?.optionText || '---'
            }) : option);
            
            // 현재까지 선택된 옵션들(0부터 optionIndex까지)로 variant 필터링
            const selectedOptionsKeyValueMap = {};
            for (let i = 0; i <= optionIndex; i++) {
                const option = updatedOptions[i];
                if (option.selectedIndex !== null) {
                    selectedOptionsKeyValueMap[option.optionName] = option.value[option.selectedIndex]?.optionText || '---';
                }
            }
            
            const filteredVariants = allVariants.filter(variant => {
                return Object.entries(selectedOptionsKeyValueMap).every(([key, value]) => variant.options[key] === value);
            });
            
            // 다음 옵션이 있으면 선택지 필터링
            if (optionIndex + 1 < updatedOptions.length) {
                const nextOption = updatedOptions[optionIndex + 1];
                const prevNextOptionValue = nextOption.value;
                const newNextOptionValue = prevNextOptionValue.filter(value => 
                    filteredVariants.some(variant => variant.options[nextOption.optionName] === value.optionText)
                );
                const newNextOptionValueWithSoldout = newNextOptionValue.map(value => {
                    const allFilteredVariantsWithValue = filteredVariants.filter(variant => variant.options[nextOption.optionName] === value.optionText);
                    if (allFilteredVariantsWithValue.every(variant => variant.soldout)) {
                        return {
                            ...value,
                            soldout: true
                        };
                    } else {
                        return {
                            ...value,
                            soldout: false
                        };
                    }
                });
                updatedOptions[optionIndex + 1] = { 
                    ...nextOption,
                    value: newNextOptionValueWithSoldout
                };
            }
            
            return updatedOptions;
        });
        return;     // C-false로 종료
    }

    /**
     * 렌더링할 옵션 리스트 반환
     * 선택 불가능한 옵션은 비활성화 표시
     */
    getRenderableOptions(productOptions, allVariants) {
        return productOptions
            .filter(option => (!option.isTextOption && !option.isFileOption))
            .map((option, index) => ({
                ...option,
                disabled: !this.canSelectOption(index, productOptions)
            }));
    }
}

